<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl w-full space-y-8 bg-white dark:bg-gray-800 p-10 rounded-xl shadow-2xl">
      <div class="grid md:grid-cols-2 gap-8 items-center">
        <!-- Left Section: Benefits -->
        <div class="benefits-section p-6 bg-blue-50 dark:bg-blue-900 rounded-lg">
          <h2 class="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-6">Unlock Full Access!</h2>
          <ul class="space-y-3 text-gray-700 dark:text-gray-300">
            <li class="flex items-start">
              <svg class="h-6 w-6 text-green-500 dark:text-green-400 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span>Access to all <strong>full exam</strong> sets.</span>
            </li>
            <li class="flex items-start">
              <svg class="h-6 w-6 text-green-500 dark:text-green-400 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span>Detailed performance tracking and history.</span>
            </li>
            <li class="flex items-start">
              <svg class="h-6 w-6 text-green-500 dark:text-green-400 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span>Personalized study recommendations (coming soon!).</span>
            </li>
            <li class="flex items-start">
              <svg class="h-6 w-6 text-green-500 dark:text-green-400 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span>Priority support.</span>
            </li>
          </ul>
          <p class="mt-6 text-sm text-gray-600 dark:text-gray-400">
            Sign up today to supercharge your exam preparation!
          </p>
        </div>

        <!-- Right Section: Login/Signup & Firebase Config -->
        <div class="login-section p-6">
          <div>
            <img class="mx-auto h-12 w-auto" src="/vite.svg" alt="Exam Engine Logo">
            <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100">
              Access Your Account
            </h2>
            <p class="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Or <router-link to="/exam-select/Y6/Maths" class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">continue with a sample exam</router-link>
            </p>
          </div>

          <!-- Firebase Login Module Placeholder -->
          <form class="mt-8 space-y-6" @submit.prevent="handleLoginOrSignup">
            <div class="rounded-md shadow-sm -space-y-px">
              <div>
                <label for="email-address" class="sr-only">Email address</label>
                <input id="email-address" name="email" type="email" autocomplete="email" required v-model="email" class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 dark:bg-gray-700 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Email address">
              </div>
              <div>
                <label for="password" class="sr-only">Password</label>
                <input id="password" name="password" type="password" autocomplete="current-password" required v-model="password" class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 dark:bg-gray-700 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Password">
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <input id="remember-me" name="remember-me" type="checkbox" class="h-4 w-4 text-blue-600 dark:bg-gray-700 dark:border-gray-600 focus:ring-blue-500 border-gray-300 rounded">
                <label for="remember-me" class="ml-2 block text-sm text-gray-900 dark:text-gray-300"> Remember me </label>
              </div>

              <div class="text-sm">
                <a href="#" class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"> Forgot your password? </a>
              </div>
            </div>

            <div>
              <button type="submit" class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                Sign in / Sign up with Email
              </button>
            </div>
             <p v-if="errorMsg" class="text-center text-sm text-red-600 dark:text-red-400">
              {{ errorMsg }}
            </p>
          </form>
          <div class="mt-4">
            <button type="button" @click="signInWithGoogle" class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 border-gray-300 dark:border-gray-500">
              <img class="w-5 h-5 mr-2" src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google icon">
              Sign in with Google
            </button>
          </div>

          <!-- Firebase NPM Settings Placeholder - Can be removed or kept for user reference -->
          <div class="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Firebase Status</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Firebase should be configured. If you encounter issues, ensure your <code>src/firebaseConfig.js</code> is correct.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
 import { ref, computed } from 'vue'; // Added computed here
 import { useRouter, useRoute } from 'vue-router';
 import { auth, googleProvider } from '../firebaseConfig'; // Import googleProvider
 import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup } from "firebase/auth"; // Import signInWithPopup

 const router = useRouter();
 const route = useRoute();

 const email = ref('');
 const password = ref('');
 const errorMsg = ref('');

// Placeholder for intended exam redirection after login
 const intendedExamPath = computed(() => {
  const { year, subject, setId } = route.query;
   if (year && subject && setId) {
     return `/exam/${year}/${subject}/${setId}?mode=full`;
   }
   return '/'; // Default redirect if no specific exam was targeted
 });

 const handleLoginOrSignup = async () => {
   errorMsg.value = ''; // Clear previous errors
   if (!email.value || !password.value) {
     errorMsg.value = 'Please enter both email and password.';
     return;
   }
   try {
     // Try to sign in first
     await signInWithEmailAndPassword(auth, email.value, password.value);
     router.push(intendedExamPath.value);
   } catch (signInError) {
     // If sign in fails, try to create a new user
     // If sign in fails, try to create a new user if the error indicates the user doesn't exist
     // or if it's a general invalid credential error (which might also mean user doesn't exist).
     if (signInError.code === 'auth/user-not-found' || signInError.code === 'auth/invalid-credential') {
       try {
         await createUserWithEmailAndPassword(auth, email.value, password.value);
         router.push(intendedExamPath.value);
       } catch (signUpError) {
         // Handle specific signup errors, e.g., email already in use by another account (though unlikely if sign-in failed)
         // or weak password if not caught by Firebase UI.
         errorMsg.value = `Signup Error: ${signUpError.message}`;
       }
     } else if (signInError.code === 'auth/wrong-password') {
       errorMsg.value = 'Incorrect password. Please try again.';
     } else {
       // Handle other sign-in errors
       errorMsg.value = `Login Error: ${signInError.message}`;
     }
   }
 };

 const signInWithGoogle = async () => {
   errorMsg.value = ''; // Clear previous errors
   try {
     await signInWithPopup(auth, googleProvider);
     router.push(intendedExamPath.value);
   } catch (error) {
     errorMsg.value = `Google Sign-In Error: ${error.message}`;
   }
 };

// Add dark mode handling if needed, though App.vue should cover global styles
 import { useDarkMode } from '../composables/useDarkMode';
 useDarkMode(); // Ensures dark mode is initialized for this view
</script>

<style scoped>
/* Add any component-specific styles here if needed */
.benefits-section {
  /* Example: Add a subtle pattern or image if desired */
}
</style>